import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Calendar, MapPin, FileText, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { cacheGet, cacheSet } from "../../lib/cache";
import BackButton from "../../components/BackButton";
import { Spinner, Alert, formatRupiah } from "../../components/ui";

export default function CustomerBookingCreate() {
  const { workerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const workerServiceId = searchParams.get("worker_service_id") || "";
  const serviceName = searchParams.get("service_name") || "";
  const price = searchParams.get("price") || "";

  const [form, setForm] = useState({
    scheduled_at: "",
    location: "",
    customer_note: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workerInfo, setWorkerInfo] = useState(() => cacheGet(`worker-svc:${workerId}`).data);

  useEffect(() => {
    const fetchWorker = async () => {
      try {
        const { data } = await api.get(`/api/workers/${workerId}/services`);
        setWorkerInfo(data);
        cacheSet(`worker-svc:${workerId}`, data, 300000);
      } catch (err) {
        // ignore
      }
    };
    if (workerId) fetchWorker();
  }, [workerId]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        worker_service_id: workerServiceId,
      };
      if (form.scheduled_at) {
        payload.scheduled_at = new Date(form.scheduled_at).toISOString();
      }
      if (form.location) payload.location = form.location;
      if (form.customer_note) payload.customer_note = form.customer_note;

      const { data } = await api.post("/api/bookings", payload);
      navigate(`/customer/bookings/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!workerServiceId) {
    return (
      <div className="animate-fade-up grid place-items-center py-8">
        <div className="container space-y-6">
          <BackButton to="/customer/services" />
          <div className="card p-10 text-center">
            <p className="font-bold text-brand-800">Data tidak lengkap</p>
            <p className="text-brand-400 mt-1">Pilih layanan terlebih dahulu untuk membuat pesanan.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <div className="container space-y-6">
        <BackButton to={-1} />

        <div>
          <h1 className="text-brand-800">Buat Pesanan</h1>
          <p className="text-brand-400 mt-1">Isi detail pesanan Anda</p>
        </div>

        {error && <Alert kind="error">{error}</Alert>}

        {/* Service Summary */}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brand-400">Layanan</p>
              <p className="font-bold text-brand-800">{serviceName || "Layanan"}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-brand-400">Estimasi Harga</p>
              <p className="text-xl font-extrabold text-brand-800">
                {price ? formatRupiah(Number(price)) : "Hubungi pekerja"}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="card p-6 space-y-5">
          <div>
            <label className="field-label flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Jadwal
            </label>
            <input
              type="datetime-local"
              name="scheduled_at"
              value={form.scheduled_at}
              onChange={handleChange}
              className="field-input"
              data-testid="input-scheduled"
            />
          </div>

          <div>
            <label className="field-label flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Lokasi / Alamat
            </label>
            <input
              type="text"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="Alamat layanan"
              className="field-input"
              data-testid="input-location"
            />
          </div>

          <div>
            <label className="field-label flex items-center gap-2">
              <FileText className="h-4 w-4" /> Catatan
            </label>
            <textarea
              name="customer_note"
              value={form.customer_note}
              onChange={handleChange}
              placeholder="Deskripsi tambahan untuk pekerja (opsional)"
              rows={3}
              className="field-input resize-none"
              data-testid="input-note"
            />
          </div>

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={loading}
            data-testid="submit-booking"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Mengirim...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Kirim Pesanan <ArrowRight className="h-4 w-4" />
              </span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
